import React, { useEffect, useRef, useState } from "react";
import { X, Flower2, Wine, Gift, Gem, Coins } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { GoldButton } from "./common";

export const GIFT_ICONS = { flower: Flower2, wine: Wine, gift: Gift, gem: Gem };

export function GiftModal({ connectionId, recipientName, onClose, onSent }) {
  const [catalog, setCatalog] = useState({ gifts: [], recipient_share_pct: 50 });
  const [balance, setBalance] = useState(null);
  const [pick, setPick] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const reqId = useRef(`gr_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);

  useEffect(() => {
    api.get("/gifts/catalog").then((r) => setCatalog(r.data)).catch(() => {});
    api.get("/credits/balance").then((r) => setBalance(r.data.credits)).catch(() => {});
  }, []);

  const insufficient = pick && balance != null && balance < pick.credits;

  const send = async () => {
    if (!pick) return;
    setBusy(true);
    try {
      const { data } = await api.post("/gifts", { gift_id: pick.id, connection_id: connectionId, message: note || null, client_request_id: reqId.current });
      toast.success(`${pick.label} sent`, { description: `${data.credits_spent || 0} credits spent.` });
      onSent && onSent();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send gift");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="gift-modal">
      <div className="w-full max-w-md db-glass rounded-3xl border border-[#D4AF37]/25 p-6 db-fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4">
          <div><div className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37]">Digital Gift</div><h3 className="font-serif text-2xl text-[#FBF8F3] mt-1">Send {recipientName} a gift</h3></div>
          <button onClick={onClose} className="text-[#9E978E]" data-testid="gift-modal-close"><X size={22} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {catalog.gifts.map((g) => {
            const Icon = GIFT_ICONS[g.icon] || Gift;
            const active = pick?.id === g.id;
            return (
              <button key={g.id} data-testid={`gift-option-${g.id}`} onClick={() => setPick(g)}
                className={`rounded-2xl border p-4 text-left transition-colors ${active ? "border-[#D4AF37]/70 bg-[#541224]/40" : "border-white/10 bg-[#0C0A0A]/50 hover:border-[#D4AF37]/40"}`}>
                <Icon size={22} className="text-[#D4AF37] mb-2" />
                <div className="text-sm text-[#FBF8F3]">{g.label}</div>
                <div className="text-xs text-[#C9A96E] flex items-center gap-1 mt-0.5"><Coins size={11} /> {g.credits} credits</div>
              </button>
            );
          })}
        </div>
        <input data-testid="gift-note-input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="Add a short note (optional)"
          className="w-full mt-4 rounded-xl bg-[#0C0A0A]/60 border border-[#D4AF37]/20 focus:border-[#D4AF37]/60 outline-none px-4 py-2.5 text-sm text-[#FBF8F3] placeholder:text-[#9E978E]" />
        <p className="text-[11px] text-[#9E978E] mt-3">You pay the full gift price. {recipientName} earns {catalog.recipient_share_pct}% as earned credits.{balance != null && ` Your balance: ${balance} credits.`}</p>
        {insufficient && <p className="text-xs text-[#E58CA0] mt-2" data-testid="gift-insufficient">You need {pick.credits - balance} more credits for this gift.</p>}
        <GoldButton data-testid="gift-send-btn" onClick={send} disabled={!pick || busy || insufficient} className="w-full mt-4">
          {busy ? "Sending…" : pick ? `Send ${pick.label} · ${pick.credits} credits` : "Choose a gift"}
        </GoldButton>
      </div>
    </div>
  );
}

export function GiftShowcase({ userId }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/gifts/showcase/${userId}`).then((r) => setData(r.data)).catch(() => {}); }, [userId]);
  if (!data || data.total === 0) return null;
  return (
    <div className="mt-6" data-testid="gift-showcase">
      <div className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] mb-2">Gifts Received · {data.total}</div>
      <div className="flex flex-wrap gap-2">
        {data.gifts.map((g) => {
          const Icon = GIFT_ICONS[g.icon] || Gift;
          return (
            <span key={g.gift_id} data-testid={`gift-showcase-${g.gift_id}`} className="flex items-center gap-1.5 rounded-full bg-[#541224]/40 border border-[#D4AF37]/30 px-3 py-1 text-sm text-[#FBF8F3]">
              <Icon size={14} className="text-[#D4AF37]" /> {g.label} <span className="text-[#C9A96E]">×{g.count}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

export function ThankYouButton({ m, onSent }) {
  const [busy, setBusy] = useState(false);
  if (m.gift?.thanked) return <div data-testid={`gift-thanked-${m.message_id}`} className="text-[11px] text-[#6FD79E] mt-1">Thank-you sent</div>;
  const send = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/gifts/${m.gift.gift_txn_id}/thank-you`);
      onSent(data.message);
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Could not send thank-you");
      setBusy(false);
    }
  };
  return (
    <button data-testid={`gift-thank-you-btn-${m.message_id}`} onClick={send} disabled={busy}
      className="mt-2 text-xs rounded-full px-3 py-1 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 disabled:opacity-50">
      {busy ? "Sending…" : "Say thank you (free)"}
    </button>
  );
}
